import React from 'react';
import classNames from 'classnames';
import styles from './NavigationPageList.module.scss';
import { Container, Link, Picture } from 'ui/components';

export interface NavigationPageListItem {
    name?: string;
    url?: string;
    thumbnail?: string;
    resume?: string;
}

export interface NavigationPageListProps {
    className?: string;
    linkboxItems?: NavigationPageListItem[];
    showThumbnails?: boolean;
    showResume?: boolean;
    showAZBar?: boolean;
}

export const NavigationPageList: React.FC<NavigationPageListProps> = ({
    className,
    linkboxItems = [],
    showThumbnails = false,
    showResume = false,
    showAZBar = false,
}) => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ'.split('');

    const sortedItems = showAZBar
        ? [...linkboxItems].sort((a, b) =>
              (a.name ?? '').localeCompare(b.name ?? '', 'da')
          )
        : linkboxItems;

    const itemLetters = new Set(
        sortedItems.map((item) => item.name?.trim().charAt(0).toUpperCase())
    );

    return (
        <Container width="Standard">
            <div className={classNames(styles.NavigationPageList, className)}>
                {showAZBar && (
                    <nav
                        className={styles.NavigationPageList_azBar}
                        aria-label="Alfabetisk navigation"
                    >
                        <ul role="list" className={styles.NavigationPageList_azList}>
                            {alphabet.map((letter) => {
                                const hasItems = itemLetters.has(letter);

                                return (
                                    <li
                                        role="listitem"
                                        key={letter}
                                        className={styles.NavigationPageList_azItem}
                                    >
                                        <a
                                            href={hasItems ? `#letter-${letter}` : undefined}
                                            className={classNames(
                                                styles.NavigationPageList_azLink,
                                                {
                                                    [styles.NavigationPageList_azLink___active]: hasItems,
                                                    [styles.NavigationPageList_azLink___inactive]: !hasItems,
                                                }
                                            )}
                                        >
                                            {letter}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>
                )}

                <ul role="list" className={styles.NavigationPageList_list}>
                    {sortedItems.map((item, index) => {
                        const firstLetter = item.name?.trim().charAt(0).toUpperCase();
                        const prevLetter =
                            index > 0
                                ? sortedItems[index - 1].name?.trim().charAt(0).toUpperCase()
                                : null;

                        const isNewLetter = showAZBar && firstLetter !== prevLetter;

                        return (
                            <React.Fragment key={index}>
                                {isNewLetter && firstLetter && (
                                    <li
                                        id={`letter-${firstLetter}`}
                                        className={styles.NavigationPageList_letterHeader}
                                    >
                                        <p className={styles.NavigationPageList_letterTitle}>
                                            {firstLetter}
                                        </p>
                                    </li>
                                )}

                                <li role="listitem" className={styles.NavigationPageList_item}>
                                    <Link
                                        ariaLabel={item.name}
                                        url={item.url}
                                        className={styles.NavigationPageList_link}
                                    >
                                        {showThumbnails && item.thumbnail && (
                                            <Picture
                                                url={item.thumbnail}
                                                isCover={true}
                                                lazyload={true}
                                                removeAlt={true}
                                                className={styles.NavigationPageList_thumbnail}
                                            />
                                        )}

                                        <div className={styles.NavigationPageList_content}>
                                            <h2 className={styles.NavigationPageList_title}>
                                                {item.name}
                                            </h2>

                                            {showResume && item.resume && (
                                                <p className={styles.NavigationPageList_resume}>
                                                    {item.resume}
                                                </p>
                                            )}
                                        </div>
                                    </Link>
                                </li>
                            </React.Fragment>
                        );
                    })}
                </ul>
            </div>
        </Container>
    );
};




